import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { debounce } from '@mui/material/utils';
import { FieldValues, useController } from 'react-hook-form';

import { AutocompleteField } from 'common/forms/Field';
import {
    InputOptionSkillsDocument,
    ISkillInputOptionFragment,
} from 'graphql/types';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

const BASE_QUERY_VARS = {
    first: 50,
};

interface IProps {
    ids?: string[];
    label?: string;
    name: string;
    noOptionsText?: string;
}

export const SkillSelectField = <TFieldValues extends FieldValues>({
    control: formControl,
    ids,
    label,
    name,
    noOptionsText,
    ...other
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();

    const control = asTypedControl<ISkillInputOptionFragment>(
        name,
        formControl
    );

    const {
        field,
        fieldState: { error },
    } = useController({ name, control });

    const [inputValue, setInputValue] = useState('');

    const [fetchSkills, { data, loading, called }] = useLazyQuery(
        InputOptionSkillsDocument,
        { fetchPolicy: 'no-cache' }
    );

    const { skills: skillsData } = data || {};

    const skills =
        skillsData?.edges?.map((edge) => edge?.node).filter(Boolean) || [];
    const skillsCount = skillsData?.count || 0;

    const value = field.value;

    const fetch = useMemo(
        () =>
            debounce((inputValue: string) => {
                fetchSkills({
                    variables: {
                        ...BASE_QUERY_VARS,
                        ids,
                        q: inputValue || undefined,
                    },
                });
            }, 400),
        [fetchSkills, ids]
    );

    useEffect(() => {
        // The first query is done by opening, so we want to avoid calling it twice
        if (!called) return;

        fetch(inputValue);
    }, [inputValue, fetch]);

    const handleOpen = () => {
        if (!!skills.length) return;

        fetchSkills({
            variables: {
                ...BASE_QUERY_VARS,
                ids,
                q: inputValue || undefined,
            },
        });
    };

    return (
        <AutocompleteField
            {...other}
            {...field}
            error={!!error}
            filterOptions={(options) => options}
            getOptionLabel={(option) => option.name}
            helperText={error?.message}
            InputProps={{
                label: label ?? translate('skill'),
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={loading}
            loadingText={translate('loadingDots')}
            noOptionsText={
                !loading
                    ? (noOptionsText ?? translate('noOptionsText.filterSkills'))
                    : translate('loadingDots')
            }
            options={skills}
            slotProps={{
                listbox: {
                    component: ({
                        children,
                        ...other
                    }: React.ComponentProps<'ul'>) => (
                        <ul {...other}>
                            {children}
                            {skillsCount > BASE_QUERY_VARS.first && (
                                <Box
                                    className="MuiAutocomplete-option"
                                    component="li"
                                    key="hasMore"
                                    sx={{
                                        pointerEvents: 'none',
                                        opacity: 0.6,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            width: '100%',
                                            textAlign: 'center',
                                        }}
                                        variant="caption"
                                    >
                                        {translate('moreResultsSelectSearch')}
                                    </Typography>
                                </Box>
                            )}
                        </ul>
                    ),
                },
            }}
            value={value || null}
            onChange={(_event, value) => {
                field.onChange(value);
            }}
            onInputChange={(_event, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                    setInputValue(newInputValue);
                }
            }}
            onOpen={handleOpen}
        />
    );
};
